import React, { useRef, useState, useEffect, useContext } from "react";
import "./FormComponents.css";
import { MyContext } from "../../../../Context/MyContext";
import ImageCropper from "./ImageCropper";
import AppIcon from "../../../../Components/AppIcon";

const isPdfValue = (v) => {
  if (!v) return false;
  if (v instanceof File) return v.type === "application/pdf" || /\.pdf$/i.test(v.name || "");
  if (typeof v === "string") return /\.pdf($|\?)/i.test(v);
  if (typeof v === "object" && typeof v.default === "string") return /\.pdf($|\?)/i.test(v.default);
  return false;
};

const fileLabel = (v) => {
  if (!v) return "Document";
  if (v instanceof File) return v.name || "Selected file";
  if (typeof v === "string") return v.split("/").pop() || "Document";
  if (typeof v === "object" && v.name) return v.name;
  return "Document";
};

// Convert any value → preview descriptor array: { src, isPdf, name }
const toPreviewArray = (value, imgUrl) => {
  if (!value && value !== 0) return [];

  const convert = (v) => {
    if (!v) return null;
    const pdf = isPdfValue(v);
    if (v instanceof File) {
      return {
        src: pdf ? null : URL.createObjectURL(v),
        isPdf: pdf,
        name: fileLabel(v),
        href: URL.createObjectURL(v),
      };
    }
    if (typeof v === "string") {
      const href = imgUrl ? imgUrl(v) : v;
      return { src: pdf ? null : href, isPdf: pdf, name: fileLabel(v), href };
    }
    if (typeof v === "object" && typeof v.default === "string") {
      const href = v.default;
      const pdfObj = /\.pdf($|\?)/i.test(href);
      return { src: pdfObj ? null : href, isPdf: pdfObj, name: fileLabel(href), href };
    }
    return null;
  };

  if (Array.isArray(value)) return value.map(convert).filter(Boolean);
  const single = convert(value);
  return single ? [single] : [];
};

const FileUpload = ({
  label,
  field,
  value,
  onChange,
  isrequired = false,
  multi_upload = false,
  accept = "image/*",
  error,
  className = "",
  enableCrop = false,
  cropAspect = 4 / 3,
}) => {
  const { imgUrl } = useContext(MyContext);
  const inputRef = useRef(null);

  const [previews, setPreviews] = useState([]);
  const [drag, setDrag] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropFile, setCropFile] = useState(null);

  useEffect(() => {
    const srcs = toPreviewArray(value, imgUrl);
    setPreviews(srcs);
  }, [value]); // eslint-disable-line

  const processFiles = (files) => {
    const arr = Array.from(files);

    if (multi_upload) {
      const existing = Array.isArray(value) ? value : [];
      const updated = [...existing, ...arr];
      setPreviews(toPreviewArray(updated, imgUrl));
      if (onChange) onChange(field, updated);
    } else {
      const file = arr[0];
      if (!file) return;

      if (enableCrop && file.type.startsWith("image/")) {
        const objectUrl = URL.createObjectURL(file);
        setCropFile(file);
        setCropSrc(objectUrl);
      } else {
        setPreviews(toPreviewArray(file, imgUrl));
        if (onChange) onChange(field, file);
      }
    }
  };

  const handleCropDone = (croppedFile) => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setCropFile(null);
    setPreviews(toPreviewArray(croppedFile, imgUrl));
    if (onChange) onChange(field, croppedFile);
  };

  const handleCropCancel = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setCropFile(null);
  };

  const removePreview = (idx) => {
    setPreviews((prev) => prev.filter((_, i) => i !== idx));

    if (multi_upload) {
      const arr = Array.isArray(value) ? value : [];
      const updated = arr.filter((_, i) => i !== idx);
      if (onChange) onChange(field, updated);
    } else {
      if (onChange) onChange(field, null);
    }
  };

  const isPdfAccept = typeof accept === "string" && accept.includes("pdf");

  return (
    <>
      {cropSrc && (
        <ImageCropper
          src={cropSrc}
          fileName={cropFile?.name}
          aspect={cropAspect}
          onDone={handleCropDone}
          onCancel={handleCropCancel}
        />
      )}

      <div className={`file-upload-wrapper ${className}`}>
        {label && (
          <label className="form-label">
            {label}
            {isrequired && <span className="required-star">*</span>}
          </label>
        )}

        <div
          className={[
            "file-drop-zone",
            drag ? "drag-over" : "",
            error ? "input-error" : "",
          ].filter(Boolean).join(" ")}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            processFiles(e.dataTransfer.files);
          }}
        >
          {previews.length > 0 ? (
            <div className="file-previews">
              {previews.map((item, i) => (
                <div
                  key={i}
                  className={`preview-item${item.isPdf ? " preview-item-pdf" : ""}`}
                  onClick={(e) => {
                    if (item.isPdf && item.href) {
                      e.stopPropagation();
                      window.open(item.href, "_blank", "noopener,noreferrer");
                    }
                  }}
                >
                  {item.isPdf ? (
                    <div className="preview-pdf">
                      <AppIcon name="pdf" size={28} />
                      <span className="preview-pdf-name" title={item.name}>{item.name}</span>
                      <span className="preview-pdf-hint">Click to preview</span>
                    </div>
                  ) : (
                    <img
                      src={item.src}
                      alt={`preview-${i}`}
                      className="preview-img"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}
                  {enableCrop && !multi_upload && !item.isPdf && (
                    <span className="crop-badge" title="Image was cropped"><AppIcon name="crop" size={12} /></span>
                  )}
                  <button
                    type="button"
                    className="remove-preview"
                    onClick={(e) => {
                      e.stopPropagation();
                      removePreview(i);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}

              {multi_upload && (
                <div className="add-more-upload">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </div>
              )}
            </div>
          ) : (
            <div className="file-drop-content">
              <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <polyline points="16 16 12 12 8 16" />
                <line x1="12" y1="12" x2="12" y2="21" />
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
              </svg>
              <p className="upload-text">
                {multi_upload
                  ? "Click or drag to upload images"
                  : isPdfAccept
                  ? "Click or drag to upload PDF"
                  : enableCrop
                  ? "Click or drag to upload & crop"
                  : "Click or drag to upload"}
              </p>
              <span className="upload-hint">
                {isPdfAccept
                  ? "PDF documents only"
                  : enableCrop && !multi_upload
                  ? "You'll crop the image after selecting"
                  : "Please choose to upload"}
              </span>
            </div>
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multi_upload}
          style={{ display: "none" }}
          onChange={(e) => {
            processFiles(e.target.files);
            e.target.value = "";
          }}
        />

        {error && <span className="error-msg">{error}</span>}
      </div>
    </>
  );
};

export default FileUpload;
